import { cubicBezier, motion, useReducedMotion } from "framer-motion";

import { RISK_CONFIG } from "../../constants/riskConfig";
import { ZP } from "../../constants/palette";
import { EASE } from "../PageTransition";

/**
 * One dot field, drawn in the state of one pipeline step.
 *
 * The same 8×8 field as the homepage's Chaos-to-Clarity mesh, carried onto the
 * Process page so the two read as one argument: the field arrives scattered
 * (Collect), snaps to a grid (Line up), is bounded by a service area (Locate),
 * takes a colour inside that boundary (Score), has its gaps filled with
 * labelled estimates (Estimate), and is redrawn with the old one kept behind
 * it (Refresh).
 *
 * Colour appears at Score and nowhere else — a measured result earns it. The
 * estimate rings stay the ink colour at reduced strength, never a band colour.
 *
 * Each step loops for as long as `play` is on: the step plays in, holds,
 * eases back out, rests, and begins again. Every element of every step shares
 * one cycle, so staggered elements stay in phase with each other however long
 * the column is left open. Each step's "out" lands where its next "in" begins,
 * or somewhere the jump between them is invisible, so the loop has no seam.
 *
 * Under reduced motion the field is drawn once, in its finished state, and
 * holds still. Decorative: the step's copy carries the meaning, so this is
 * hidden from assistive tech.
 */
const S = 168;
const N = 8;
const GAP = S / (N + 1);
const R = 1.75;

/** The scored band. Moderate, so the field reads as "a score", not a verdict. */
const SCORED = RISK_CONFIG["0"].hex;
const ACCENT = "#2563EB";

/**
 * The cycle, in seconds, and where in it the shared phases fall. The "in"
 * moves are staggered across roughly the first half; everything holds until
 * `OUT_AT`, eases out together until `REST_AT`, and rests until the cycle
 * restarts. Long enough to read, short enough not to feel like waiting.
 */
export const CYCLE = 6.4;

/**
 * How every loop repeats. framer-motion hands opacity (and only opacity, of
 * what these drawings animate) to the browser's own animation engine, and
 * there a looping keyframe track with `times` ran at about 1.5× speed: on the
 * About page the fades finished a second or more ahead of the moves beside
 * them, which framer runs on its own clock. framer skips the browser path
 * whenever a repeat has a delay, so each repeat takes a negligible one
 * (a tenth of a millisecond, taken off the duration so the cycle's length
 * is unchanged) and every property stays on the one clock.
 *
 * Measured, not assumed: sampled in the browser, the track, tick and label
 * fades were on schedule only after this.
 */
const REPEAT_DELAY = 0.0001;
export const loopRepeat = (length = CYCLE) => ({
  duration: length - REPEAT_DELAY,
  repeat: Infinity,
  repeatDelay: REPEAT_DELAY,
});
export const LOOP_REPEAT = loopRepeat(CYCLE);
export const LOOP_EASE = cubicBezier(...EASE);
const OUT_AT = 4.7;
const REST_AT = 5.8;

/**
 * The shared cycle: its length, where the shared ease-out starts, and where
 * the rest begins. A drawing that needs a longer story — the About page's —
 * passes its own to `loop` as `cycle`.
 */
export const SHARED_CYCLE = { length: CYCLE, outAt: OUT_AT, restAt: REST_AT };

/**
 * Keyframes for one element: at `from` until `at`, moving to `to` over `dur`,
 * holding, moving to `out` between OUT_AT and REST_AT, then holding there
 * until the cycle wraps back to `from`. `out` defaults to `from`, which makes
 * the loop breathe — in, hold, back, rest. `ease` defaults to the site curve;
 * pass "linear" for a move that has to keep pace with something stepping.
 * `cycle` swaps in a different timeline (see SHARED_CYCLE).
 */
export const loop = (
  from,
  to,
  { at = 0, dur = 0.8, out = from, ease = LOOP_EASE, cycle = SHARED_CYCLE } = {},
) => {
  const { length, outAt, restAt } = cycle;
  const start = Math.min(at, outAt - 0.2);
  const end = Math.min(at + dur, outAt - 0.1);
  const times = [0, start, end, outAt, restAt, length].map((t) => t / length);
  const animate = {};
  Object.keys(to).forEach((k) => {
    animate[k] = [from[k], from[k], to[k], to[k], out[k], out[k]];
  });
  return {
    initial: from,
    animate,
    transition: { ...loopRepeat(length), times, ease },
  };
};

/* A deterministic jitter, so the scattered state is the same on every render
   and on the server prerender. */
const jitter = (() => {
  let seed = 7;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const out = [];
  for (let i = 0; i < N * N; i += 1) out.push([rnd() - 0.5, rnd() - 0.5, rnd()]);
  return out;
})();

const GRID = Array.from({ length: N * N }, (_, i) => {
  const r = Math.floor(i / N) + 1;
  const c = (i % N) + 1;
  // Disorder fades out to the right, as it does on the homepage mesh.
  const spread = Math.max(0, (N - c) / N) * 26;
  const [jx, jy, k] = jitter[i];
  return {
    i,
    r,
    c,
    x: c * GAP,
    y: r * GAP,
    sx: c * GAP + jx * spread,
    sy: r * GAP + jy * spread,
    k,
  };
});

/** The service area: an irregular boundary, deliberately not a hexagon. */
const AREA = [
  [34, 30],
  [104, 22],
  [140, 52],
  [132, 104],
  [146, 140],
  [92, 148],
  [44, 132],
  [24, 86],
];
const AREA_PATH = `M${AREA.map((p) => p.join(" ")).join(" L")} Z`;

const inside = (x, y) => {
  let hit = false;
  for (let a = 0, b = AREA.length - 1; a < AREA.length; b = a, a += 1) {
    const [xa, ya] = AREA[a];
    const [xb, yb] = AREA[b];
    const aAbove = ya > y;
    const bAbove = yb > y;
    const crossesEdge = aAbove !== bAbove;
    if (crossesEdge && x < ((xb - xa) * (y - ya)) / (yb - ya) + xa) hit = !hit;
  }
  return hit;
};

/** The one "not detected" result in Line up: an empty ring, never a number. */
const isNotDetected = ({ r, c }) => r === 4 && c === 5;
/** The gaps Estimate fills. Few, on purpose — an estimate fills a gap, it is
 *  not most of the picture. */
const ESTIMATED = new Set(["2-3", "3-6", "5-2", "5-5", "6-7", "7-4", "4-4"]);
const isEstimated = ({ r, c }) => ESTIMATED.has(`${r}-${c}`);

/**
 * Each step's elements, as `{ key, el, attrs, from, to, opts }`: `el` is the
 * SVG tag, `attrs` are the fixed attributes, and from/to/opts feed `loop`.
 * Built once per ink colour; the field never changes shape.
 */
const elements = (kind, ink) => {
  const els = [];
  const add = (key, el, attrs, from, to, opts) =>
    els.push({ key, el, attrs, from, to, opts });
  const dot = { r: R, fill: ink };
  const ring = { r: 3.5, fill: "none", stroke: ink, strokeWidth: 1 };

  if (kind === "collect") {
    GRID.forEach((d) => {
      const o = d.c < 5 ? 0.55 + d.k * 0.45 : 0.25;
      add(
        d.i,
        "circle",
        dot,
        { cx: d.sx - 28, cy: d.sy, opacity: 0 },
        { cx: d.sx, cy: d.sy, opacity: o },
        // Fades where it stands, rather than retreating the way it came.
        {
          at: 0.1 + d.c * 0.12 + d.k * 0.3,
          dur: 1,
          out: { cx: d.sx, cy: d.sy, opacity: 0 },
        },
      );
    });
    // One reading lights up as the last of them arrives.
    add(
      "accent",
      "circle",
      { cx: GRID[17].sx, cy: GRID[17].sy, r: 3, fill: ACCENT },
      { opacity: 0 },
      { opacity: 1 },
      { at: 2.4, dur: 0.4 },
    );
  }

  if (kind === "lineup") {
    GRID.forEach((d) => {
      if (isNotDetected(d)) return;
      add(
        d.i,
        "circle",
        dot,
        { cx: d.sx, cy: d.sy, opacity: d.c < 5 ? 0.8 : 0.3 },
        { cx: d.x, cy: d.y, opacity: 1 },
        { at: 0.3 + d.k * 0.4, dur: 0.9 },
      );
    });
    GRID.filter(isNotDetected).forEach((d) =>
      add(
        "nd",
        "circle",
        { ...ring, cx: d.x, cy: d.y },
        { opacity: 0 },
        { opacity: 1 },
        {
          at: 1.6,
          dur: 0.5,
        },
      ),
    );
  }

  if (kind === "locate") {
    add(
      "area",
      "path",
      {
        d: AREA_PATH,
        fill: "none",
        stroke: ink,
        strokeWidth: 1.2,
        strokeLinejoin: "round",
      },
      { pathLength: 0, opacity: 1 },
      { pathLength: 1, opacity: 1 },
      { at: 0.7, dur: 1.3, out: { pathLength: 1, opacity: 0 } },
    );
    GRID.forEach((d) =>
      add(
        d.i,
        "circle",
        { ...dot, cx: d.x, cy: d.y },
        { opacity: 1 },
        { opacity: inside(d.x, d.y) ? 1 : 0.18 },
        { at: 1.7, dur: 0.6 },
      ),
    );
    // The address pin, dropped before the boundary draws around it.
    add(
      "pin",
      "g",
      {},
      { y: -14, opacity: 0 },
      { y: 0, opacity: 1 },
      { at: 0.2, dur: 0.5, out: { y: 0, opacity: 0 } },
    );
  }

  if (kind === "score") {
    add("area", "path", {
      d: AREA_PATH,
      fill: "none",
      stroke: ink,
      strokeWidth: 1.2,
      strokeLinejoin: "round",
    });
    GRID.forEach((d) => {
      if (!inside(d.x, d.y)) {
        add(d.i, "circle", { ...dot, cx: d.x, cy: d.y, opacity: 0.18 });
        return;
      }
      // Coloured from the centre outward.
      const dist = Math.hypot(d.x - 86, d.y - 86);
      add(
        d.i,
        "circle",
        { cx: d.x, cy: d.y },
        { fill: ink, r: R },
        { fill: SCORED, r: 2.25 },
        { at: 0.4 + dist * 0.02, dur: 0.6 },
      );
    });
  }

  if (kind === "estimate") {
    GRID.forEach((d) => {
      if (!isEstimated(d)) add(d.i, "circle", { ...dot, cx: d.x, cy: d.y });
    });
    GRID.filter(isEstimated).forEach((d, n) =>
      add(
        `est-${d.i}`,
        "circle",
        { ...ring, cx: d.x, cy: d.y },
        { opacity: 0 },
        { opacity: 0.5 },
        {
          at: 0.4 + n * 0.3,
          dur: 0.6,
        },
      ),
    );
  }

  if (kind === "refresh") {
    /* The previous field slides back and dims as the new one draws in over
       it: old scores are kept, not overwritten. It fades away only as the
       cycle ends — at the restart the new field is the old one, drawn at full
       strength in the same place, so the swap is invisible. */
    GRID.forEach((d) =>
      add(
        `old-${d.i}`,
        "circle",
        dot,
        { cx: d.x, cy: d.y, opacity: 1 },
        { cx: d.x + 7, cy: d.y - 7, opacity: 0.22 },
        { at: 0.3, dur: 1, out: { cx: d.x + 7, cy: d.y - 7, opacity: 0 } },
      ),
    );
    GRID.forEach((d) =>
      add(
        `new-${d.i}`,
        "circle",
        { ...dot, cx: d.x, cy: d.y },
        { opacity: 0 },
        { opacity: 1 },
        { at: 0.5 + d.c * 0.12, dur: 0.5, out: { opacity: 1 } },
      ),
    );
  }

  return els;
};

const PIN = (ink) => (
  <>
    <path
      d="M84 70c-3.6 0-6.5 2.8-6.5 6.4 0 4.6 6.5 10.6 6.5 10.6s6.5-6 6.5-10.6c0-3.6-2.9-6.4-6.5-6.4Z"
      fill="none"
      stroke={ink}
      strokeWidth={1.2}
      strokeLinejoin="round"
    />
    <circle cx={84} cy={76.4} r={2} fill={ink} />
  </>
);

const TAGS = { circle: motion.circle, path: motion.path, g: motion.g };

export default function StepMotif({
  kind,
  play = true,
  ink = ZP.ink,
  className = "h-[168px] w-[168px]",
}) {
  const reduce = useReducedMotion();
  const moving = play && !reduce;

  return (
    <svg
      viewBox={`0 0 ${S} ${S}`}
      className={className}
      aria-hidden="true"
      focusable="false"
      data-testid={`motif-${kind}`}
    >
      {elements(kind, ink).map(({ key, el, attrs, from, to, opts }) => {
        const Tag = TAGS[el];
        const children = el === "g" ? PIN(ink) : null;
        if (!from)
          return (
            <Tag key={key} {...attrs}>
              {children}
            </Tag>
          );
        // Still: reduced motion holds the finished state; a field not yet in
        // view waits at the start of its cycle.
        if (!moving) {
          return (
            <Tag key={key} {...attrs} initial={false} animate={reduce ? to : from}>
              {children}
            </Tag>
          );
        }
        return (
          <Tag key={key} {...attrs} {...loop(from, to, opts)}>
            {children}
          </Tag>
        );
      })}
    </svg>
  );
}
